import { ActionText, Banner, colorBySeverity, type MessageSeverity } from "@mdotm/shadcn/legacy/components";
import { type Ref, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { sortBanners } from "./sortBanners";
import type { BannerItem } from "./types";

/** A ring in the banner's severity colour that swells and fades once. */
function pulseKeyframes(severity: MessageSeverity): Keyframe[] {
	const color = colorBySeverity[severity];
	return [
		{ boxShadow: `0 0 0 0 color-mix(in srgb, ${color} 35%, transparent)` },
		{ boxShadow: `0 0 0 6px color-mix(in srgb, ${color} 0%, transparent)` },
	];
}

const pulseTiming: KeyframeAnimationOptions = { duration: 700, iterations: 1, easing: "ease-out" };

const focusableSelector =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type BannerStackHandle = {
	/** Draws the eye to the banner with this id with a short pulse. Answers whether the stack shows it. */
	pulse(id: string): boolean;
};

export type BannerStackProps = {
	/** In any order: the stack sorts them by severity, then age. */
	banners: readonly BannerItem[];
	/** Called with the id of the banner whose close button was pressed. Removing it is the caller's job. */
	onDismiss(id: string): void;
	handleRef?: Ref<BannerStackHandle>;
};

/**
 * Banners one above the other, most severe first. Renders nothing at all when there are none. After a
 * dismissal, focus moves to the close button of the next dismissible banner below, else to the first
 * focusable element after the stack.
 */
export function BannerStack({ banners, onDismiss, handleRef }: BannerStackProps): React.JSX.Element | null {
	const { t } = useTranslation();
	const containerRef = useRef<HTMLDivElement>(null);
	const closeButtons = useRef(new Map<string, HTMLButtonElement>());
	const bannerElements = useRef(new Map<string, HTMLDivElement>());
	const pendingFocus = useRef<{ dismissedId: string; target: HTMLElement | null } | null>(null);
	const sorted = sortBanners(banners);

	// Waits for the caller to remove the banner, so focus never lands on a node about to go.
	useLayoutEffect(() => {
		const pending = pendingFocus.current;
		if (pending == null || banners.some((banner) => banner.id === pending.dismissedId)) {
			return;
		}
		pendingFocus.current = null;
		pending.target?.focus();
	}, [banners]);

	useImperativeHandle(handleRef, () => ({
		pulse(id) {
			const element = bannerElements.current.get(id);
			const banner = sorted.find((item) => item.id === id);
			if (element == null || banner == null) {
				return false;
			}
			// Optional: jsdom has no Web Animations API.
			element.animate?.(pulseKeyframes(banner.severity), pulseTiming);
			return true;
		},
	}));

	if (sorted.length === 0) {
		return null;
	}

	const dismiss = (id: string) => {
		const below = sorted.slice(sorted.findIndex((banner) => banner.id === id) + 1);
		const next = below.find((banner) => banner.dismissible);
		const target = (next && closeButtons.current.get(next.id)) ?? firstFocusableAfter(containerRef.current);
		pendingFocus.current = { dismissedId: id, target };
		onDismiss(id);
	};

	return (
		<div ref={containerRef} className="flex flex-col gap-2">
			{sorted.map((banner) => (
				<div
					key={banner.id}
					ref={(element) => {
						if (element) {
							bannerElements.current.set(banner.id, element);
						} else {
							bannerElements.current.delete(banner.id);
						}
					}}
					role={banner.severity === "error" ? "alert" : "status"}
					className="break-words rounded-[3px]"
				>
					<Banner
						severity={banner.severity}
						title={banner.title}
						dismissible={banner.dismissible}
						onDismiss={() => dismiss(banner.id)}
						dismissRef={(button) => {
							if (button) {
								closeButtons.current.set(banner.id, button);
							} else {
								closeButtons.current.delete(banner.id);
							}
						}}
						i18n={{ dismissLabel: ({ title }) => t("BANNER_STACK.DISMISS", { title }) }}
					>
						{(banner.description != null || banner.action) && (
							<>
								{banner.description}
								{banner.action && (
									<>
										{" "}
										<ActionText onClick={banner.action.onClick}>{banner.action.label}</ActionText>
									</>
								)}
							</>
						)}
					</Banner>
				</div>
			))}
		</div>
	);
}

function firstFocusableAfter(container: HTMLElement | null): HTMLElement | null {
	if (container == null) {
		return null;
	}
	const focusables = container.ownerDocument.querySelectorAll<HTMLElement>(focusableSelector);
	return (
		Array.from(focusables).find(
			(element) =>
				!container.contains(element) &&
				(container.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
		) ?? null
	);
}
